import { z } from 'zod';
import { TypedApiClient } from '../index';
import { schemas } from '../../generated/api-client';
import { apiPath } from '../path';

// ====== Import schemas from generated code ======
// Public marketplace storefront reads (no auth) — organizations opted into the
// marketplace, their categories/products/units, and price types. Categories/units/
// price-types reuse the same generated Firestore-response schemas as the authenticated
// endpoints (identical shapes there); the organization/storefront and product reads use
// the narrow MarketplaceStorefrontResponse / MarketplaceProductResponse — the public
// endpoints deliberately do NOT return the full OrganizationFirestoreResponse or
// ProductFirestoreResponse, which carry private billing/contact fields and merchant cost
// price / supplier / internal ids no anonymous caller should see.
const {
  MarketplaceStorefrontResponse,
  MarketplaceProductResponse,
  ProductCategoryFirestoreResponse,
  ProductUnitFirestoreResponse,
  PriceTypeFirestoreResponse,
} = schemas;

// ====== Re-export schemas for external use ======
// Narrow storefront-safe schema (FIX 1) — backend's MarketplacePublicController no longer
// returns the full OrganizationFirestoreResponse (which carries private billing/contact
// fields); it returns this narrow MarketplaceStorefrontResponse shape instead.
export const MarketplaceOrganizationSchema = MarketplaceStorefrontResponse;
// Narrow storefront-safe product schema (round 3 fix) — backend's MarketplacePublicController
// no longer returns the full ProductFirestoreResponse (which carries purchasePrice/supplierID/
// internal staff ids); it returns this narrow MarketplaceProductResponse shape instead.
export const MarketplaceProductSchema = MarketplaceProductResponse;
export const MarketplaceCategorySchema = ProductCategoryFirestoreResponse;
export const MarketplaceUnitSchema = ProductUnitFirestoreResponse;
export const MarketplacePriceTypeSchema = PriceTypeFirestoreResponse;

// ====== Type Exports ======
export type MarketplaceOrganization = z.infer<typeof MarketplaceStorefrontResponse>;
export type MarketplaceProduct = z.infer<typeof MarketplaceProductResponse>;
export type MarketplaceCategory = z.infer<typeof ProductCategoryFirestoreResponse>;
export type MarketplaceUnit = z.infer<typeof ProductUnitFirestoreResponse>;
export type MarketplacePriceType = z.infer<typeof PriceTypeFirestoreResponse>;

/**
 * Public marketplace storefront API factory (no auth required).
 * Mirrors the backend's `MarketplacePublicController` (`/marketplace/*`).
 */
export function createMarketplaceApi(client: TypedApiClient) {
  return {
    getOrganizations: (): Promise<MarketplaceOrganization[]> => {
      return client.get('/marketplace/organizations', {
        responseSchema: z.array(MarketplaceStorefrontResponse),
      });
    },

    getOrganization: (organizationId: string): Promise<MarketplaceOrganization> => {
      return client.get(apiPath`/marketplace/organizations/${organizationId}`, {
        responseSchema: MarketplaceStorefrontResponse,
      });
    },

    getCategories: (organizationId: string): Promise<MarketplaceCategory[]> => {
      return client.get('/marketplace/product-categories', {
        params: { organizationId },
        responseSchema: z.array(ProductCategoryFirestoreResponse),
      });
    },

    getCategory: (organizationId: string, categoryId: string): Promise<MarketplaceCategory> => {
      return client.get(apiPath`/marketplace/product-categories/${categoryId}`, {
        params: { organizationId },
        responseSchema: ProductCategoryFirestoreResponse,
      });
    },

    getProducts: (organizationId: string, categoryId?: string): Promise<MarketplaceProduct[]> => {
      return client.get('/marketplace/products', {
        params: categoryId ? { organizationId, categoryId } : { organizationId },
        responseSchema: z.array(MarketplaceProductResponse),
      });
    },

    getProductUnits: (organizationId: string): Promise<MarketplaceUnit[]> => {
      return client.get('/marketplace/product-units', {
        params: { organizationId },
        responseSchema: z.array(ProductUnitFirestoreResponse),
      });
    },

    getPriceTypes: (organizationId: string): Promise<MarketplacePriceType[]> => {
      return client.get('/marketplace/price-types', {
        params: { organizationId },
        responseSchema: z.array(PriceTypeFirestoreResponse),
      });
    },
  };
}
